/**
 * Events menu: clickable list of events/custom events to go inside mp-drop-menu
 */
import debounce from 'lodash/debounce';
import pick from 'lodash/pick';
import produce from 'immer';

import {assertUnreachable, findKeyByValue} from 'common/util/typescript';
import {ContextPanelData} from 'common/widgets/context-panel/types';
import {SPECIAL_EVENTS} from 'common/report/constants';
import {getIconForEvent, renameEvent} from 'common/report/util/events';
import {getProfilePath} from 'common/report/util/profile/utils';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {TRACKING_EVENTS} from 'common/widgets/query-block/constants';
import {SortOptions} from 'common/widgets/query-block/properties-menu/types';
import {ResourceType} from 'common/report/constants';
import {eventPropertyValuesClient} from 'common/report/queries/query-api/event-property-values-client';
import {MPProperties} from 'common/constants';
import {ExpanderItemClickEvent, ItemClickEvent, Section, SectionItem} from 'common/components/items-menu/types';
import {promptCustomEvents} from 'common/widgets/custom-event-modal';
import {getProjectId, getWorkspaceId} from 'common/report/util';
import {customEventsStore} from 'common/report/stores/custom-events';
import {ChangeEvent} from 'common/components/select/types';

import {ChildMenu} from '../child-menu';
import {EventsController, getCustomEventMenuItem, getEventMenuItem} from '../contexts/events-controller';
import {template} from './template';
import {
  ActionType,
  EVENT_PROPERTIES,
  EventItem,
  EventMenuItem,
  MenuType,
  ModalType,
  StageableMenuItem,
  TriggerableMenuItem,
} from './types';
import {renderEventItemContextPanel} from './context-panel';

interface EventsMenuState {
  eventData: EventItem[];
  isMenuLoading: boolean;
  isMenuOpen: boolean;
  tooltipData: ContextPanelData;
  permissions: Set<string>;
  stagedItemsStack: StageableMenuItem[];
  includeHidden: boolean;
  openedAt: number;
  sortValue: string;
}
export class EventsMenu extends ChildMenu<EventsMenuState> {
  static contextPanelRenderer = renderEventItemContextPanel;

  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      'show-any-event': `boolean`,
      'show-session-event': `boolean`,
      'show-all-events': `boolean`,
      'show-top-events': `boolean`,
    };
  }

  attributeChangedCallback(attr, oldValue, newValue) {
    super.attributeChangedCallback(attr, oldValue, newValue);

    if (attr === `open`) {
      this.update({
        includeHidden: false,
        sortValue: this.config.defaultState.sortValue,
      });
      if (this.attr(`open`)) {
        this.trackEvent(TRACKING_EVENTS.OPEN_EVENTS_DROPDOWN);
        this.update({openedAt: new Date().getTime()});
      }
    }
  }

  get config() {
    return {
      template,

      contexts: [`eventsController` as const],
      defaultContexts: {eventsController: new EventsController()},

      defaultState: {
        eventData: [],
        tooltipData: null,
        includeHidden: false,
        stagedItemsStack: [],
        openedAt: null,
        sortValue: `Suggested`,
      },

      helpers: {
        ...super.config.helpers,
        getMenuSections: () => this.getSections(),
        showItemDetails: debounce((ev) => this.showItemDetails(ev.detail.item), 100),
        isLoading: () => this.getContext(`eventsController`).loading,
        hasSelectSortingHeader: (): boolean => {
          return !!this.getContext(`eventsController`).isNewMenu();
        },
        getSortingOptionSectionItems: () => {
          return Object.entries(SortOptions).map(([label, value]) => ({
            label,
            value,
            isSelected: label === this.state.sortValue,
          }));
        },
        handleSortChange: ({detail}: ChangeEvent) => {
          this.update({sortValue: detail.value});
        },

        /** appends a manual event entry item */
        sectionsFromInput: (searchFilter): Array<Section<EventItem>> => [
          {
            items: [
              {
                label: searchFilter,
                value: {
                  value: searchFilter,
                  label: searchFilter,
                },
                icon: `plus`,
              },
            ],
          },
        ],
      },
    };
  }

  getItemUniqueKey(item: EventItem) {
    return item.value;
  }

  handleCustomEventDelete(deletedId: number) {
    if (deletedId && this.attr(`selected`)?.id === deletedId) {
      // if deleting the selected custom event we delete the entire block
      this.dispatchEvent(new CustomEvent(`delete`));
    }
  }

  handleCloseMenu() {
    this.helpers.showItemDetails.cancel();
    this.resetStagedItems();
  }

  handleExpandItemClick({detail: {section}}: ExpanderItemClickEvent) {
    if (section.label === `Hidden Events`) {
      this.trackEvent(TRACKING_EVENTS.LEXICON_SHOW_HIDDEN);
    }
  }

  async triggerModal(item: TriggerableMenuItem): Promise<void> {
    if (item.modalType === ModalType.CreateCustomEvent) {
      const {appliedValue} = await promptCustomEvents({
        open: true,
        'all-data-groups': this.attr(`all-data-groups`),
        'project-has-people': this.attr(`project-has-people`),
        'project-id': getProjectId(),
        'workspace-id': getWorkspaceId(),
        'track-name': this.attr(`track-name`),
      });

      if (appliedValue) {
        const eventItem = getCustomEventMenuItem(appliedValue).value;
        this.selectItem(eventItem);
      }
    }
  }

  selectItem(
    rawItem: EventItem,
    {searchFilter = ``, labelMatch = {}, subLabelMatch = {}, customMatch = {}, topScrollPosition = 0} = {},
  ) {
    const item = pick(rawItem, EVENT_PROPERTIES);
    if (!findKeyByValue(SPECIAL_EVENTS, item.value)) {
      this.addRecentItem(ResourceType.Event, item);
    }

    const trackingProps = this.trackingProps(
      item,
      searchFilter,
      labelMatch,
      subLabelMatch,
      customMatch,
      topScrollPosition,
    );
    this.trackSelection(trackingProps);
    this.dispatchEvent(new CustomEvent(`select`, {detail: {item}}));
    this.resetStagedItems();
  }

  handleClickItem({detail}: ItemClickEvent<EventMenuItem>) {
    const {item} = detail;
    /** Signal is bypassing the sections for this dropdown, return whatever Signal provided */
    if (this.attr(`sections`)?.length > 0) {
      this.dispatchEvent(new CustomEvent(`select`, {detail: {item: item.value}}));
      return;
    }

    switch (item.value.action) {
      case ActionType.StageMenu:
        this.stageItem(item.value);
        break;
      case ActionType.TriggerModal:
        this.triggerModal(item.value);
        break;
      case ActionType.Select:
      case undefined:
        this.selectItem(item.value, detail);
        break;
      default:
        assertUnreachable(item.value);
    }
  }

  private async getSampleEventsUrl(eventItem) {
    const projectId = this.attr(`project-id`);
    const workspaceId = this.attr(`workspace-id`);

    const eventName = eventItem?.value || eventItem;

    // Ignore tag and invalid items
    if (eventItem?.value?.type === `tag` || typeof eventName !== `string`) {
      return ``;
    }

    const results = await eventPropertyValuesClient.query({
      eventName,
      propertyName: MPProperties.DistinctId,
      projectId,
      workspaceId,
      limit: 10,
    });

    if (!results?.length) {
      return ``;
    }

    const distinctId = results[Math.floor(Math.random() * results.length)];
    const url = getProfilePath(distinctId, null, {
      includedEvents: [eventName],
    });

    return url;
  }

  private getTooltipData(
    item: EventItem,
    {isLoadingAction = false, sampleEventUrl = ``, canProvideSampleUrl = true} = {},
  ): ContextPanelData {
    return this.getContext(`eventsController`).getContextPanelData(item, {
      allowEdit: !this.attr(`readonly`) && !this.attr(`hide-edit-controls`),
      isLoadingAction,
      sampleEventUrl,
      canProvideSampleUrl,
    });
  }

  async showItemDetails(item: SectionItem<EventItem>) {
    let eventItem = item.value;
    // @ts-ignore TODO: Zihe will refactor signal so this transformation is not needed.
    if (item?.value?.kind === `event`) {
      eventItem = item.value;
    }

    this.update({tooltipData: this.getTooltipData(eventItem, {isLoadingAction: true})});

    const sampleEventUrl = await this.getSampleEventsUrl(eventItem);
    // The menu might have been closed while waiting for the async request to complete. In this case, setting
    // `tooltipData` would cause `mp-query-block` to show the context panel (`tooltipData` is shared between this
    // component and `mp-query-block`)
    // NOTE: That we check `isMenuOpen` rather than this.attr(`open`) because there's a delay before attributes are
    // updated, so checking the attr could still lead to a race condition.
    if (this.state.isMenuOpen) {
      this.update({
        tooltipData: this.getTooltipData(eventItem, {
          sampleEventUrl,
          canProvideSampleUrl: !!sampleEventUrl,
          isLoadingAction: false,
        }),
      });
    }
  }

  private getStagedItem(): StageableMenuItem {
    const {stagedItemsStack} = this.state;
    return stagedItemsStack[stagedItemsStack.length - 1];
  }

  private stageItem(item: StageableMenuItem): void {
    this.produceUpdate((draftState) => {
      draftState.stagedItemsStack.push(item);
    });
  }

  private resetStagedItems(): void {
    this.update({stagedItemsStack: []});
  }

  getSelectedHoverTooltipData(options): ContextPanelData {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }
    return this.getTooltipData(
      selected,
      options || {
        isLoadingAction: true,
      },
    );
  }

  async prefetchTooltipOptions() {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const sampleEventUrl = await this.getSampleEventsUrl(selected);

    return {
      sampleEventUrl,
      canProvideSampleUrl: !!sampleEventUrl,
      isLoadingAction: false,
    };
  }

  getSelectedTrackingProps(): Obj {
    const selected = this.attr(`selected`);
    if (!selected) {
      return null;
    }

    const props = {'Entity type': `event`};
    if (selected.custom) {
      props[`Is custom`] = true;
    }
    return props;
  }

  getPersistenceKey() {
    return `events-menu`;
  }

  getSections() {
    // TODO: Zihe remove this check, a quick and temp workaround for signal to display context panel
    if (this.attr(`sections`)?.length > 0) {
      return this.attr(`sections`);
    }

    return [
      ...(!this.getContext(`eventsController`).isNewMenu() ? [this.getRecentsSection()] : []),
      ...this.getContext(`eventsController`).getSections({
        stagedItem: this.getStagedItem(),
        dataGroupId: this.attr(`data-group-id`),
        excludeAutotrackEvents: this.attr(`exclude-autotrack-events`),
        excludeCustomEvents: this.attr(`exclude-custom-events`),
        hideEditControls: this.attr(`hide-edit-controls`),
        mainSectionShowLimit: this.getContext(`eventsController`).isNewMenu() ? 8 : 0,
        selected: this.attr(`selected`),
        showAllEvents: this.attr(`show-all-events`),
        showAnyEvent: this.attr(`show-any-event`),
        showSessionEvent: this.attr(`show-session-event`),
        showSpecialDatasetProperty: this.attr(`show-special-dataset-property`),
        showTopEvents: this.attr(`show-top-events`),
        sortConfig: this.helpers.hasSelectSortingHeader() ? SortOptions[this.state.sortValue] : null,
        recentlyUsedEvents: this.getRecentItems(ResourceType.Event),
      }),
    ].filter(Boolean);
  }

  getRecentsSection(): Section<EventItem> {
    const eventData = this.getContext(`eventsController`).data;
    const recentItems = this.getRecentItems(ResourceType.Event, 3) as EventItem[];
    return {
      label: `Recently viewed`,
      items: recentItems
        .map((event) => {
          // prevents deleted events from showing up in recently selected items
          const isCustomEvent = Boolean(event?.id);
          const eventItem = eventData.get(event?.value) ?? (isCustomEvent ? null : getEventMenuItem(event));
          if (eventItem) {
            return produce(eventItem, (updatedEventItem) => {
              updatedEventItem.value.isRecentlyViewed = true;
            });
          } else {
            return null;
          }
        })
        .filter(Boolean),
    };
  }

  selectedIcon() {
    return getIconForEvent(this.state.selected);
  }

  selectedLabel() {
    const {selected} = this.state;
    let selectedLabel = ``;

    if (selected) {
      const {renamed, label, labelPrefix} = selected;

      // handle custom event loading
      if (selected.id && selected.custom) {
        selectedLabel = customEventsStore.value[selected.id]?.name || selected.label || `Loading...`;
      } else {
        selectedLabel = label || renameEvent(selected.value);
      }

      if (renamed) {
        selectedLabel = `${renamed} (${selectedLabel})`;
      }

      return labelPrefix ? `${labelPrefix} ${selectedLabel}` : selectedLabel;
    }

    return selectedLabel;
  }

  trackingProps(selected, searchFilter, labelMatch, subLabelMatch, customMatch, topscrollPosition) {
    return {
      eventName: `Select Event`,
      props: {
        'Event name': selected.label,
        'from tag': this.getStagedItem()?.menuType === MenuType.Tag,
        'top scroll': topscrollPosition,
        'is custom event': !!selected.custom,
        'is autotrack event': !!selected.isCollectEverything,
        'is hidden entity': !!selected.hidden,
        'is recently viewed': !!selected.isRecentlyViewed,
        'Search - filter': searchFilter,
        'Search - label match': labelMatch,
        'Search - sublabel match': subLabelMatch,
        'Search - custom match': customMatch,
        'Metadata Search Enabled': true,
        'Elapsed time (ms) since menu opened': new Date().getTime() - this.state.openedAt,
      },
    };
  }
}

export const EventsMenuEl = defineAndMakeJsxFactory(`mp-events-menu`, EventsMenu);
